import { ActivityType } from "encamp-prisma/generated/browser";
import invariant from "tiny-invariant";

import {
  LEGACY_STAFF_ACTIVITY_DESCRIPTIONS,
  LegacyDescription,
  StaffActivityDescriptions,
} from "./constants";
import { allStaffActivitiesByDescription } from "./states";
import { ActivityDependency, StaffActivityDetails } from "./types";

export interface DependencyResult {
  met: boolean;
  pending: ActivityDependency[];
}

export function getStaffActivityByDescription(
  description: StaffActivityDescriptions
): StaffActivityDetails {
  const activity = allStaffActivitiesByDescription[description];
  invariant(activity, `Couldn't find activity for ${description}`);
  return activity;
}

export function getStaffActivityByLegacyDescription(
  description: LegacyDescription
): StaffActivityDetails {
  const newDescription = LEGACY_STAFF_ACTIVITY_DESCRIPTIONS[description];
  return getStaffActivityByDescription(newDescription);
}

/**
 * Get the dependencies for a staff activity by its legacy description.
 * Returns an empty array if the activity has no dependencies or is not found.
 *
 * Note: This intentionally returns [] for unknown activities rather than throwing.
 * This allows the UI to treat unknown activities as having no dependencies,
 * which is the safe/permissive default (allows completion).
 */
export function getActivityDependencies(description: string): ActivityDependency[] | null {
  try {
    const activity = Object.keys(LEGACY_STAFF_ACTIVITY_DESCRIPTIONS).includes(description)
      ? getStaffActivityByLegacyDescription(description as LegacyDescription)
      : getStaffActivityByDescription(description as StaffActivityDescriptions);
    if (activity.jurisdictionType !== "City/County") {
      // State activities should be done in order
      return null;
    }
    return activity.dependsOn ?? [];
  } catch {
    // Activity not found in toscanini config - treat as no dependencies
    return null;
  }
}

/**
 * Check if all dependencies for an activity are met.
 * @param description - The legacy description of the activity to check
 * @param workflowActivities - All activities in the workflow with their status
 * @returns Object with `met` boolean and `pending` array of unmet dependency names
 */
export function areDependenciesMet(
  description: string,
  workflowActivities: Array<{
    name: ActivityType | string;
    description: string;
    status: string;
  }>
): DependencyResult {
  const dependencies = getActivityDependencies(description);

  if (!dependencies || dependencies.includes("Prior Activity")) {
    return { met: false, pending: [] };
  }

  if (dependencies.length === 0) {
    return { met: true, pending: [] };
  }

  const pending: ActivityDependency[] = [];

  for (const dep of dependencies) {
    // Check if dependency is met - match by ActivityType (name) or description
    const matchingActivity = workflowActivities.find(
      (a) => a.name === dep || a.description === dep
    );

    if (!matchingActivity || matchingActivity.status !== "Completed") {
      pending.push(dep);
    }
  }

  return {
    met: pending.length === 0,
    pending,
  };
}
